import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FaLock, FaUser, FaShieldAlt } from 'react-icons/fa';
import { toast } from 'sonner';
import { API, setAdminSession, getToken, formatApiError } from '@/lib/adminAuth';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (getToken()) {
      navigate('/admin/preinscriptions');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      if (data.token) {
        setAdminSession(data.token, data.username);
        toast.success('Connexion réussie');
        navigate('/admin/preinscriptions');
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || 'Identifiants invalides';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-secondary via-blue-950 to-brand-primary flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md p-8 shadow-2xl bg-white" data-testid="admin-login-card">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaShieldAlt className="text-3xl text-brand-primary" />
          </div>
          <h1 className="text-2xl font-bold text-brand-secondary">Espace Administrateur</h1>
          <p className="text-gray-600 mt-2">Université CEFAT International</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <Label htmlFor="username" className="text-gray-700 font-medium">Nom d'utilisateur</Label>
            <div className="relative mt-2">
              <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <Input
                id="username"
                type="text"
                data-testid="admin-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Entrez votre identifiant"
                className="pl-10"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-gray-700 font-medium">Mot de passe</Label>
            <div className="relative mt-2">
              <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <Input
                id="password"
                type="password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Entrez votre mot de passe"
                className="pl-10"
                required
              />
            </div>
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>

        <p className="text-xs text-gray-400 text-center mt-6">
          Accès réservé au personnel autorisé
        </p>
      </Card>
    </div>
  );
}
